import { ErrorMessages } from '@app/shared/constants';

export enum SsoProtocol {
  OIDC = 'oidc',
}

export enum SsoConnectionMode {
  TENANT = 'TENANT',
  CONSUMER = 'CONSUMER',
}

/** Mirrors MICROSOFT_CONSUMER_TENANT_ID on SsoConnectionEntity. Personal Microsoft accounts all
 *  live in this fixed Microsoft-owned tenant. A component test pins the literal. */
export const MICROSOFT_CONSUMER_TENANT_ID = '9188040d-6c67-4c5b-b112-36a304b66dad';

/** Mirrors GUID_REGEX on the CreateSsoConnectionDto so a bad tenant id is caught before the request. */
export const SSO_TENANT_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Mirrors HOSTNAME_REGEX: one bare hostname per entry, no scheme, no '@', no wildcard. */
export const SSO_ALLOWED_DOMAIN_PATTERN = /^(?!-)[a-z0-9-]{1,63}(?:\.[a-z0-9-]{1,63})+$/i;

/** A client secret at or inside this many days of expiry (or already expired) is flagged in the list. */
export const SSO_SECRET_EXPIRY_WARNING_DAYS = 30;

/**
 * Read shape returned by the admin endpoints. The client secret is write-only and never present;
 * `issuer` is derived server-side from `expectedTenantId` and is read-only here.
 */
export interface SsoConnection {
  uuid: string;
  name: string;
  protocol: SsoProtocol;
  mode: SsoConnectionMode;
  issuer: string;
  expectedTenantId: string;
  clientId: string;
  scopes: string;
  allowedDomains: string[];
  isActive: boolean;
  clientSecretExpiresAt: string;
  createdAt: string;
  updatedAt: string;
}

export interface SsoConnectionListItem extends SsoConnection {
  usedByCompanyCount: number;
}

export interface SsoConnectionDetail extends SsoConnection {
  usedByCompanies: string[];
}

/** POST body. No `issuer` key: the server derives it and rejects a supplied value. */
export interface CreateSsoConnectionPayload {
  name: string;
  mode?: SsoConnectionMode;
  expectedTenantId: string;
  clientId: string;
  clientSecret: string;
  clientSecretExpiresAt: string;
  allowedDomains: string[];
  scopes?: string;
  isActive?: boolean;
}

/**
 * PUT body. No `issuer` key. `clientSecret` is omitted entirely when the admin does not rotate the
 * secret, which leaves the stored value untouched; when it is present `clientSecretExpiresAt` must
 * accompany it.
 */
export interface UpdateSsoConnectionPayload {
  name?: string;
  mode?: SsoConnectionMode;
  expectedTenantId?: string;
  clientId?: string;
  clientSecret?: string;
  clientSecretExpiresAt?: string;
  allowedDomains?: string[];
  scopes?: string;
  isActive?: boolean;
}

/** Splits the comma / whitespace separated domain input into the array the DTO expects. */
export function parseAllowedDomains(raw: string | null | undefined): string[] {
  return (raw || '')
    .split(/[,\s]+/)
    .map((domain) => domain.trim().toLowerCase())
    .filter((domain) => domain.length > 0);
}

/** Whole days from now until the secret expires; negative once it has expired. */
export function daysUntilSecretExpiry(expiresAt: string | Date | null | undefined): number | null {
  if (!expiresAt) {
    return null;
  }
  const expiry = new Date(expiresAt).getTime();
  if (isNaN(expiry)) {
    return null;
  }
  return Math.floor((expiry - Date.now()) / (24 * 60 * 60 * 1000));
}

/** True when the secret expires within the warning window or has already expired. */
export function isSecretExpiring(expiresAt: string | Date | null | undefined): boolean {
  const days = daysUntilSecretExpiry(expiresAt);
  return days !== null && days <= SSO_SECRET_EXPIRY_WARNING_DAYS;
}

/** Pulls a displayable message out of whatever the API service rethrew. */
export function ssoErrorMessage(error: any, fallback: string = ErrorMessages.UNEXPECTED): string {
  if (typeof error === 'string') {
    return error;
  }
  if (typeof error?.message === 'string') {
    return error.message;
  }
  return fallback;
}
